import React, { useEffect, useState } from "react";
import { Dialog, Fab, Slide } from "@mui/material";
import { Chat as ChatIcon } from "@mui/icons-material";
import type { TransitionProps } from "@mui/material/transitions";
import { useChat } from "./hooks/useChat";
import { ChatWidgetProps } from "./types";
import { ChatHeader } from "./components/ChatHeader";
import { NicknameEditor } from "./components/NicknameEditor";
import { ChatMessages } from "./components/ChatMessages";
import { ChatInput } from "./components/ChatInput";
import { sanitizeEmailNickname } from "../../utils/generals";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ChatWidget(props: ChatWidgetProps) {
  const { botName = "Asistente" } = props;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const {
    state,
    setState,
    messagesEndRef,
    messagesContainerRef,
    sendMessage,
    handleScroll,
  } = useChat(props);

  // Pre-position scroll to bottom BEFORE opening the widget
  const handleOpenWidget = () => {
    console.log("🎯 Preparing to open widget - pre-positioning scroll");

    // First, set the widget to open
    setOpen(true);

    // Then immediately position scroll to bottom (before animation starts)
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        console.log("✅ Pre-positioned scroll to bottom");
      }
    }, 10); // Very short delay to ensure DOM is ready
  };

  const manageScrooll = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        console.log("✅ Pre-positioned scroll to bottom");
      }
    }, 10);
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageToSend = inputValue;
    setInputValue("");
    manageScrooll()
    await sendMessage(messageToSend);
    manageScrooll()
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleStartEditNickname = () => {
    setState((prev) => ({
      ...prev,
      tempNickname: prev.nickname,
      isEditingNickname: true,
    }));
  };

  const handleSaveNickname = () => {
    setState((prev) => ({
      ...prev,
      nickname: prev.tempNickname,
      isEditingNickname: false,
    }));
  };

  const handleCancelNickname = () => {
    setState((prev) => ({
      ...prev,
      tempNickname: "",
      isEditingNickname: false,
    }));
  };

  const handleTempNicknameChange = (value: string) => {
    const key = sanitizeEmailNickname(props.email)
    localStorage.setItem(key, value);
    setState((prev) => ({ ...prev, tempNickname: value }));
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        sx={{
          position: "fixed",
          bottom: 84,
          right: 34,
          zIndex: 1000,
        }}
        onClick={handleOpenWidget}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            height: "80vh",
            maxHeight: "600px",
            margin: 2,
          },
        }}
      >
        <ChatHeader botName={botName} onClose={() => setOpen(false)} />

        <NicknameEditor
          nickname={state.nickname}
          isEditing={state.isEditingNickname}
          tempNickname={state.tempNickname}
          onStartEdit={handleStartEditNickname}
          onSave={handleSaveNickname}
          onCancel={handleCancelNickname}
          onTempNicknameChange={handleTempNicknameChange}
        />

        <ChatMessages
          messages={state.messages}
          isTyping={state.isTyping}
          isLoadingHistory={state.isLoadingHistory}
          historyDisabled={state.historyDisabled}
          botName={botName}
          messagesContainerRef={messagesContainerRef}
          messagesEndRef={messagesEndRef}
          onScroll={handleScroll}
          isOpen={open}
        />

        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSendMessage}
          onKeyPress={handleKeyPress}
          disabled={state.isTyping}
        />
      </Dialog>
    </>
  );
}
